import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { LayoutDashboard, User, Settings, Shield, Home, ShoppingCart, History, FileText, Clock, Menu, X, MessageSquare, Headphones, BarChart3, Users, GraduationCap, DollarSign, Bell, Lock, UserCircle, UserCog, CreditCard, LogOut } from 'lucide-react';
import { selectSidebarOpen, setSidebarOpen, selectSidebarCollapsed, toggleSidebarCollapsed } from '@store/slices/uiSlice';
import { selectCurrentPlan } from '@store/slices/subscriptionSlice';
import { useAuth } from '@hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@hooks/usePermissions';
import { usePlanRestrictions } from '@hooks/usePlanRestrictions';
import { ROUTES, USER_ROLES } from '@constants';
import logo from '@images/logo.png';
import clsx from 'clsx';
import PlanUpgradeModal from '@components/common/PlanUpgradeModal';
import FeatureRestrictionModal from '@components/common/FeatureRestrictionModal';
import FowBrand from '@components/common/FowBrand';

const Sidebar = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const sidebarOpen = useSelector(selectSidebarOpen);
  const sidebarCollapsed = useSelector(selectSidebarCollapsed);
  const currentPlan = useSelector(selectCurrentPlan);
  const { isAuthenticated, user, logout } = useAuth();
  const { hasPermission, isSuperAdmin } = usePermissions();
  const { canAccessFeature } = usePlanRestrictions();
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [isFeatureRestrictionModalOpen, setIsFeatureRestrictionModalOpen] = useState(false);
  const [restrictedFeatureName, setRestrictedFeatureName] = useState('');

  const handleLogout = async () => {
    await logout();
    navigate(ROUTES.LOGIN, { replace: true });
  };

  const getDisplayPlanName = () => {
    if (!currentPlan) return 'Free';

    const planName = currentPlan.name || currentPlan.slug || 'Free';

    switch (planName.toLowerCase()) {
      case 'free':
        return 'Free';
      case 'basic':
        return 'Basic';
      case 'pro':
        return 'Pro';
      case 'premium':
        return 'Premium';
      default:
        return planName.charAt(0).toUpperCase() + planName.slice(1).toLowerCase();
    }
  };

  if (!isAuthenticated) return null;

  // Build menu sections based on user role
  let sections = [];

  if (user?.role === USER_ROLES.ADMIN) {
    const adminItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: ROUTES.ADMIN_DASHBOARD, permission: null },
  { icon: GraduationCap, label: 'Discover Portfolios', path: ROUTES.ADMIN_STUDENTS, permission: 'VIEW_STUDENTS' },
  { icon: Users, label: 'Mentors', path: ROUTES.ADMIN_MENTORS, permission: 'VIEW_MENTORS' },
  { icon: DollarSign, label: 'Payments', path: ROUTES.ADMIN_PAYMENTS, permission: 'VIEW_PAYMENTS' },
  { icon: CreditCard, label: 'Subscriptions', path: ROUTES.ADMIN_SUBSCRIPTIONS, permission: 'VIEW_PAYMENTS' },
  { icon: ShoppingCart, label: 'Manage Carts', path: ROUTES.ADMIN_CARTS, permission: 'VIEW_CARTS' },
  { icon: MessageSquare, label: 'Feedback', path: ROUTES.ADMIN_FEEDBACK, permission: 'VIEW_FEEDBACK' },
  { icon: Headphones, label: 'Support', path: ROUTES.ADMIN_SUPPORT, permission: 'VIEW_SUPPORT' },
  { icon: UserCog, label: 'Manage Admins', path: ROUTES.ADMIN_MANAGEMENT, permission: 'MANAGE_ADMINS' },
];

    const filteredAdminItems = adminItems.filter(item => {
      if (!item.permission) return true;
      return isSuperAdmin || hasPermission(item.permission);
    });

    sections = [
      {
        label: 'ADMIN PANEL',
        items: filteredAdminItems,
      },
      {
        label: 'ACCOUNT',
        items: [
          { icon: User, label: 'Profile', path: ROUTES.PROFILE },
        ],
      },
    ];
  } else if (user?.role === USER_ROLES.STUDENT) {
    sections = [
  {
    label: 'MAIN',
    items: [
      { icon: Home, label: 'Home', path: ROUTES.HOME },
      { icon: LayoutDashboard, label: 'Dashboard', path: ROUTES.STUDENT_DASHBOARD },
      { icon: Users, label: 'Explore Portfolios', path: '/students' },
      { icon: Users, label: 'Mentor', path: ROUTES.STUDENT_MENTOR, dataTour: 'mentor' },
      { icon: MessageSquare, label: 'Requests', path: ROUTES.STUDENT_REQUESTS },
      { icon: Clock, label: 'Question History', path: ROUTES.STUDENT_MY_QUESTIONS },
    ],
  },
  {
    label: 'ACCOUNT',
    items: [
      { icon: FileText, label: 'Notes', path: ROUTES.NOTES, feature: 'notes', dataTour: 'notes' },
      { icon: ShoppingCart, label: 'Cart', path: ROUTES.CART, feature: 'cart' },
      { icon: DollarSign, label: 'Payment History', path: ROUTES.PAYMENT_HISTORY },
      { icon: User, label: 'Profile', path: ROUTES.PROFILE },
    ],
  },
];
  } else if (user?.role === USER_ROLES.MENTOR) {
    sections = [
  {
    label: 'MAIN',
    items: [
      { icon: LayoutDashboard, label: 'Dashboard', path: ROUTES.MENTOR_DASHBOARD, dataTour: 'dashboard' },
      { icon: Users, label: 'Mentors', path: ROUTES.MENTOR_MENTORS_LIST, dataTour: 'mentors' },
      { icon: DollarSign, label: 'Earnings', path: ROUTES.MENTOR_EARNINGS, dataTour: 'earnings' },
      { icon: Bell, label: 'Notifications', path: ROUTES.MENTOR_NOTIFICATIONS, dataTour: 'notifications' },
    ],
  },
  {
    label: 'ACCOUNT',
    items: [
      { icon: User, label: 'Profile', path: ROUTES.PROFILE, dataTour: 'profile' },
    ],
  },
];
  } else {
    sections = [
      {
        label: 'MAIN',
        items: [
          { icon: LayoutDashboard, label: 'Dashboard', path: ROUTES.DASHBOARD },
          { icon: User, label: 'Profile', path: ROUTES.PROFILE },
        ],
      },
    ];
  }

  return (
    <>
      <style>{`
        .sidebar-root {
          background-color: #1a2642;
          font-family: 'Inter', sans-serif;
        }
        .sidebar-logo-box {
          background-color: #ffffff;
          border-radius: 50%;
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .sidebar-logo-text {
          color: #ffffff;
          font-weight: 700;
          font-size: 1.875rem;
          letter-spacing: 0.01em;
        }
        .plan-upgrade-card {
          background-color: #1e2d3d;
          border: 1px solid #34495e;
          border-radius: 10px;
          padding: 10px 12px;
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          transition: background 0.15s;
        }
        .plan-upgrade-card:hover {
          background-color: #253544;
        }
        .plan-check-circle {
          width: 24px;
          height: 24px;
          background-color: #22c55e;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .plan-upgrade-label {
          color: #e2e8f0;
          font-weight: 600;
          font-size: 0.75rem;
          flex: 1;
        }
        .pro-badge {
          background-color: #1e3a5f;
          color: #60a5fa;
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          padding: 2px 8px;
          border-radius: 6px;
        }
        .section-label {
          color: #7f8c8d;
          font-size: 0.65rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          padding: 0 4px;
          margin-bottom: 14px;
          margin-top: 20px;
          text-transform: uppercase;
        }
        .nav-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 14px;
          border-radius: 10px;
          text-decoration: none;
          transition: all 0.2s ease;
          color: #95a5a6;
          font-size: 0.625rem;
          font-weight: 500;
          margin-bottom: 4px;
          border: 1px solid transparent;
          outline: none !important;
          -webkit-tap-highlight-color: transparent;
        }
        @media (min-width: 640px) {
          .nav-item {
            font-size: 0.6875rem;
          }
        }
        @media (min-width: 1024px) {
          .nav-item {
            font-size: 0.875rem;
          }
        }
        .nav-item:focus,
        .nav-item:focus-visible,
        .nav-item:active {
          outline: none !important;
          box-shadow: none !important;
        }
        .nav-item:hover {
          background-color: #c33c3c;
          color: #ffffff;
          border: 1px solid #c33c3c;
        }
        .nav-item.active {
          background-color: #243556;
          color: #ffffff;
          font-weight: 600;
          border: 1px solid #4a5f7f;
        }
        .nav-item .nav-icon {
          width: 18px;
          height: 18px;
          flex-shrink: 0;
        }
        .sidebar-divider {
          border: none;
          border-top: 1px solid #1a2740;
          margin: 0;
        }
        .settings-link {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 14px;
          text-decoration: none;
          color: #7f8c8d;
          font-size: 0.875rem;
          font-weight: 500;
          transition: all 0.2s ease;
          border-radius: 10px;
        }
        .settings-link:hover {
          color: #95a5a6;
        }
        .settings-link.active {
          color: #ffffff;
        }
        .logout-button {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 14px;
          text-decoration: none;
          color: #e74c3c;
          font-size: 0.875rem;
          font-weight: 600;
          transition: all 0.2s ease;
          border-radius: 10px;
          background: transparent;
          border: none;
          cursor: pointer;
          width: 100%;
        }
        .logout-button:hover {
          background-color: #c0392b;
          color: #ffffff;
        }
        .sidebar-nav-scroll {
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
          overflow-x: hidden;
          scrollbar-width: thin;
          scrollbar-color: #34495e transparent;
        }
        .sidebar-nav-scroll::-webkit-scrollbar {
          width: 5px;
        }
        .sidebar-nav-scroll::-webkit-scrollbar-track {
          background: transparent;
        }
        .sidebar-nav-scroll::-webkit-scrollbar-thumb {
          background-color: #34495e;
          border-radius: 10px;
        }
        .sidebar-nav-scroll::-webkit-scrollbar-thumb:hover {
          background-color: #4a5f7f;
        }
      `}</style>

      {/* Sidebar */}
      <aside
        className={clsx(
          'sidebar-root h-screen max-h-screen transition-all duration-300 flex flex-col flex-shrink-0 overflow-hidden',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
          'fixed left-0 top-0 z-50 lg:relative lg:translate-x-0'
        )}
        style={{ width: sidebarCollapsed ? '80px' : 'min(240px, 80vw)' }}
      >
        {/* Logo Section */}
        <div className="flex-shrink-0" style={{ padding: '20px 16px 28px' }}>
          <div className="flex items-center justify-between">
            {!sidebarCollapsed ? (
              <>
                <Link
                  to={ROUTES.DASHBOARD}
                  style={{ display: 'flex', alignItems: 'center', gap: '12px', textDecoration: 'none' }}
                >
                  <div className="sidebar-logo-box">
                    <img src={logo} alt="Logo" style={{ width: '48px', height: '48px', objectFit: 'contain' }} />
                  </div>
                  <span className="sidebar-logo-text"><FowBrand size="xsmall" /></span>
                </Link>
                {/* Close button - visible on mobile and desktop */}
                <button
                  onClick={() => {
                    // On mobile, close sidebar completely
                    if (window.innerWidth < 1024) {
                      dispatch(setSidebarOpen(false));
                    } else {
                      // On desktop, collapse to icon-only
                      dispatch(toggleSidebarCollapsed());
                    }
                  }}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                  aria-label="Collapse sidebar"
                >
                  <Menu className="w-5 h-5 text-white" />
                </button>
              </>
            ) : (
              <div className="flex flex-col items-center w-full">
                <Link to={ROUTES.DASHBOARD} className="mb-3">
                  <div className="sidebar-logo-box">
                    <img src={logo} alt="Logo" style={{ width: '48px', height: '48px', objectFit: 'contain' }} />
                  </div>
                </Link>
                {/* Expand button - visible when collapsed */}
                <button
                  onClick={() => dispatch(toggleSidebarCollapsed())}
                  className="p-2 rounded-lg hover:bg-white/10 transition-colors"
                  aria-label="Expand sidebar"
                >
                  <Menu className="w-5 h-5 text-white" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Plan Upgrade Card - Only for Students */}
        {!sidebarCollapsed && user?.role === USER_ROLES.STUDENT && (
          <div className="flex-shrink-0" style={{ padding: '0 16px 8px' }}>
            <div className="plan-upgrade-card" onClick={() => setIsPlanModalOpen(true)}>
              <div className="plan-check-circle">
                {/* Checkmark SVG */}
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                  <path d="M2.5 6.5L5.5 9.5L10.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span className="plan-upgrade-label">Plan Upgrade</span>
              <span className="pro-badge">{getDisplayPlanName()}</span>
            </div>
          </div>
        )}

        {/* Navigation Menu - scrolls internally so all items stay reachable on short screens */}
        <nav className="sidebar-nav-scroll" style={{ padding: '0 16px' }}>
          {sections.map((section) => (
            <div key={section.label}>
              {!sidebarCollapsed && section.label && <div className="section-label"> </div>}
              {section.items.map((item) => {
                const Icon = item.icon;
                const hasAccess = item.feature ? canAccessFeature(item.feature) : true;
                const isActive =
                  location.pathname === item.path ||
                  (item.path === '/students' && location.pathname === '/students') ||
                  (item.path === ROUTES.ADMIN_DASHBOARD && location.pathname === ROUTES.ADMIN_DASHBOARD) ||
                  (item.path === ROUTES.ADMIN_MENTORS && location.pathname.startsWith('/admin/mentors')) ||
                  (item.path === ROUTES.ADMIN_STUDENTS && location.pathname.startsWith('/admin/students')) ||
                  (item.path === ROUTES.ADMIN_PAYMENTS && (location.pathname.startsWith('/admin/payments') || location.pathname.startsWith('/admin/mentor-balances'))) ||
                  (item.path === ROUTES.ADMIN_SUBSCRIPTIONS && location.pathname.startsWith('/admin/subscriptions')) ||
                  (item.path === ROUTES.ADMIN_FEEDBACK && location.pathname.startsWith('/admin/feedback')) ||
                  (item.path === ROUTES.ADMIN_SUPPORT && location.pathname.startsWith('/admin/support')) ||
                  (item.path === ROUTES.ADMIN_MANAGEMENT && location.pathname.startsWith('/admin/admin-management')) ||
                  (item.path === ROUTES.ADMIN_ANALYTICS && location.pathname.startsWith('/admin/analytics')) ||
                  (item.path === ROUTES.STUDENT_DASHBOARD && location.pathname.startsWith('/student') && location.pathname !== '/students' && location.pathname !== ROUTES.STUDENT_MY_QUESTIONS && location.pathname !== ROUTES.STUDENT_MENTOR && location.pathname !== ROUTES.STUDENT_REQUESTS) ||
                  (item.path === ROUTES.STUDENT_MENTOR && location.pathname.startsWith('/student/mentor')) ||
                  (item.path === ROUTES.STUDENT_REQUESTS && location.pathname === ROUTES.STUDENT_REQUESTS) ||
                  (item.path === ROUTES.MENTOR_DASHBOARD && location.pathname === '/mentor') ||
                  (item.path === ROUTES.MENTOR_DASHBOARD && location.pathname.startsWith('/mentor/questions')) ||
                  (item.path === ROUTES.MENTOR_MENTORS_LIST && (location.pathname === '/mentor/mentors' || location.pathname.startsWith('/mentor/profile/'))) ||
                  (item.path === ROUTES.MENTOR_EARNINGS && location.pathname === '/mentor/earnings') ||
                  (item.path === ROUTES.MENTOR_NOTIFICATIONS && location.pathname === '/mentor/notifications') ||
                  (item.path === ROUTES.PROFILE && location.pathname.startsWith('/mentor-profile/')) ||
                  (item.path === ROUTES.HOME && location.pathname.startsWith('/portfolio/'));

                return (
                  <Link
                    key={item.label}
                    to={hasAccess ? item.path : '#'}
                    onClick={(e) => {
                      if (!hasAccess) {
                        e.preventDefault();
                        setRestrictedFeatureName(item.label);
                        setIsFeatureRestrictionModalOpen(true);
                      } else {
                        dispatch(setSidebarOpen(false));
                      }
                    }}
                    className={clsx(
                      'nav-item',
                      isActive && 'active',
                      sidebarCollapsed && 'justify-center',
                      !hasAccess && 'opacity-60 cursor-not-allowed'
                    )}
                    title={sidebarCollapsed ? item.label : (!hasAccess ? `${item.label} - Upgrade to access` : '')}
                    data-tour={item.dataTour || (item.label === 'Dashboard' ? 'dashboard' : item.label === 'Profile' ? 'profile' : item.label === 'Cart' ? 'cart' : item.label === 'Requests' ? 'requests' : undefined)}
                  >
                    <Icon className="nav-icon" />
                    {!sidebarCollapsed && (
                      <span className="flex items-center gap-2">
                        {item.label}
                        {!hasAccess && <Lock className="w-3 h-3 text-red-500" />}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Logout & Settings at Bottom */}
        <div className="flex-shrink-0">
          <hr className="sidebar-divider" />
          <div style={{ padding: '8px 16px 8px' }}>
            <button
              onClick={handleLogout}
              className={clsx('logout-button', sidebarCollapsed && 'justify-center')}
              title={sidebarCollapsed ? 'Logout' : ''}
              aria-label="Logout"
            >
              <LogOut
                style={{
                  width: '18px',
                  height: '18px',
                  flexShrink: 0,
                }}
              />
              {!sidebarCollapsed && <span>Logout</span>}
            </button>
          </div>
          <div style={{ padding: '0 16px 16px' }} data-tour="support">
            <Link
              to={ROUTES.SETTINGS}
              onClick={() => dispatch(setSidebarOpen(false))}
              className={clsx('settings-link', location.pathname === ROUTES.SETTINGS && 'active', sidebarCollapsed && 'justify-center')}
              title={sidebarCollapsed ? 'Settings' : ''}
              data-tour="settings"
            >
              <Settings
                style={{
                  width: '18px',
                  height: '18px',
                  flexShrink: 0,
                  transition: 'transform 0.3s',
                  transform: location.pathname === ROUTES.SETTINGS ? 'rotate(90deg)' : 'none',
                }}
              />
              {!sidebarCollapsed && <span>Settings</span>}
            </Link>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
          onClick={() => dispatch(setSidebarOpen(false))}
        />
      )}

      {/* Plan Upgrade Modal */}
      <PlanUpgradeModal isOpen={isPlanModalOpen} onClose={() => setIsPlanModalOpen(false)} />

      {/* Feature Restriction Modal */}
      <FeatureRestrictionModal
        isOpen={isFeatureRestrictionModalOpen}
        onClose={() => setIsFeatureRestrictionModalOpen(false)}
        onUpgrade={() => {
          setIsFeatureRestrictionModalOpen(false);
          setIsPlanModalOpen(true);
        }}
        featureName={restrictedFeatureName}
      />
    </>
  );
};

export default Sidebar;